import { memo, type ReactNode } from 'react';

import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';

export type ShareLinkModalProps = {
  title: string;
  headerSubtitle?: ReactNode;
  /** Rendered below the main panel inside the modal overlay (e.g. team invite nudge). */
  accessory?: ReactNode;

  onClose: () => void;

  /**
   * The modal body. Callers own their own scroll region and footer inside this slot — the shell
   * only provides the top-pinned, height-capped surface and the header (title + close). The slot
   * is a plain `flex min-h-0 flex-1 flex-col`, so a caller typically renders a scrollable body
   * followed by a `ShareLinkModalFooter`.
   */
  children: ReactNode;
  'data-testid'?: string;
};

export const ShareLinkModal = memo(
  ({ title, headerSubtitle, accessory, onClose, children, 'data-testid': dataTestId }: ShareLinkModalProps) => {
    return (
      <Modal
        className="mt-0 flex max-h-[calc(100vh-80px)] flex-col bg-transparent p-0 focus-visible:ring-0"
        dangerouslyBypassFocusLock
        data-testid={dataTestId}
        isOpen
        onDismiss={onClose}
        overlayClassName="flex items-start justify-center py-10"
      >
        <div className="flex min-h-0 w-full flex-col overflow-hidden rounded-6 ads:bg-raised bg-surface-1">
          <div className="flex shrink-0 flex-col px-6 pt-5 pb-1">
            <div className="flex items-center justify-between gap-4">
              <ModalTitle data-testid="SHARE_LINK_MODAL_TITLE">{title}</ModalTitle>
              <ModalCloseButton className="shrink-0" data-testid="SHARE_LINK_MODAL_CLOSE_BUTTON" onClick={onClose} />
            </div>
            {headerSubtitle}
          </div>

          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>

        {accessory}
      </Modal>
    );
  },
);

ShareLinkModal.displayName = 'ShareLinkModal';
